import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {fromLocalDateString, isToday, toLocalDateString} from "@/utils/DateUtils";

/** URL parameter with the day of the report, e.g. `?date=2026-10-27`. */
const DATE_PARAM = "date";

/** The day in the URL, or `null` when missing or not a valid `YYYY-MM-DD` date. */
const parseDay = (value: string | null): Date | null => {
    if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return null;

    const day: Date = fromLocalDateString(value);
    // Rejects dates that roll over, like 2026-02-31
    return !isNaN(day.getTime()) && toLocalDateString(day) === value ? day : null;
};

/**
 * The selected day of the day report, kept in the URL so it survives a
 * refresh and can be shared. Without (valid) parameter: today. Selecting
 * today removes the parameter.
 */
const useReportDayParam = (): [Date, (day: Date) => void] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const param: string | null = searchParams.get(DATE_PARAM);
    const selectedDay: Date = useMemo(() => parseDay(param) ?? new Date(), [param]);

    const setSelectedDay = useCallback((day: Date) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            if (isToday(day))
                next.delete(DATE_PARAM);
            else
                next.set(DATE_PARAM, toLocalDateString(day));
            return next;
        }, {replace: true});
    }, [setSearchParams]);

    return [selectedDay, setSelectedDay];
};

export default useReportDayParam;